import { PricingTeaserStrip } from "@/components/PricingTeaserStrip";
import { useEffect, useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { Phone, CheckCircle2, Star, ShieldCheck, Clock, Quote, Play, Check } from "lucide-react";
import heroImg from "@/assets/hero-colorado.jpg";
import heroVideoUrl from "@/assets/hero-colorado.mp4?url";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ContactForm } from "@/components/ContactForm";
import logo from "@/assets/colorado-web-impressions-logo.webp";

export type PpcFaq = { q: string; a: string };
export type PpcDeliverable = { title: string; body: string };
export type PpcTestimonial = {
  /** Verbatim review body */
  quote: string;
  /** Reviewer name (e.g. "Mike R.") */
  author: string;
  /** Optional city/area for local trust */
  location?: string;
  /** Optional industry/service tag — shown subtly under the author */
  service?: string;
};

export type PpcLandingProps = {
  /** Used for thank-you tracking + page title */
  slug: string;
  /** Browser tab title (kept under 60 chars when possible) */
  metaTitle: string;
  metaDescription: string;
  /** Eyebrow above H1 — short keyword tag */
  eyebrow: string;
  /** Main H1 — should mirror the ad headline 1:1 for Quality Score */
  headline: ReactNode;
  /** One-line value prop under the H1 */
  subheadline: string;
  /** 3–5 short value bullets shown next to the form */
  bullets: string[];
  /** 3 deliverables / "what you get" cards */
  deliverables: PpcDeliverable[];
  /** Pre-filled message in the contact form so the lead arrives tagged */
  prefillMessage: string;
  /** 4–6 FAQ items — answer the top objections that drive ad clicks but not conversions */
  faqs: PpcFaq[];
  /** Trust strip text, e.g. "5★ Google reviews · BBB A+ · 13+ yrs" */
  trustLine?: string;
  /** Per-LP CTA button label — defaults to "Get a free quote" */
  ctaLabel?: string;
  /** Per-LP form heading — defaults to "Get a free quote in 24 hours" */
  formHeading?: string;
  /** Per-LP form sub-heading — defaults to a generic owner-led line */
  formSubheading?: string;
  /** Per-LP testimonial card shown directly under the hero */
  testimonial?: PpcTestimonial;
  /** Optional YouTube video ID — when set, renders a lazy-loaded intro video below the hero */
  videoYouTubeId?: string;
  /** Optional heading for the video section */
  videoHeading?: string;
  /** Optional pricing tiers — when set, renders a compact pricing strip with #pricing anchor */
  pricingTiers?: PpcPricingTier[];
};

export type PpcPricingTier = {
  name: string;
  price: string;
  period?: string;
  tag?: string;
  features: string[];
  featured?: boolean;
};

/**
 * Standalone PPC landing page. No site header / footer — single conversion
 * goal. Outbound links limited to phone + privacy/terms (Google Ads policy).
 *
 * Quality Score wins:
 *  - H1 mirrors the ad headline
 *  - Form is above the fold on desktop
 *  - One CTA repeated (call OR fill form)
 *  - No nav leaks
 */
export function PpcLandingPage({
  slug,
  eyebrow,
  headline,
  subheadline,
  bullets,
  deliverables,
  prefillMessage,
  faqs,
  trustLine = "150+ ★★★★★ Reviews · Owner-Led · 13+ Years in Colorado",
  ctaLabel = "Get My Free Audit",
  formHeading = "Get a free audit (or mockup) in 24 hours",
  formSubheading = "No cost, no obligation. Chris (the owner) personally reviews every request and replies within one business day.",
  testimonial,
  videoYouTubeId,
  videoHeading = "Meet The Owner — Watch Welcome Video",
  pricingTiers,
}: PpcLandingProps) {
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [mountHeroVideo, setMountHeroVideo] = useState(false);
  const isMockup = slug === "web-design";
  const offerNoun = isMockup ? "free mockup" : "free audit";

  // Lazy-mount hero background video on desktop only — mirrors homepage Hero().
  // Mobile keeps the static image to save the ~5.9 MB download.
  useEffect(() => {
    if (mountHeroVideo) return;
    if (typeof window === "undefined") return;
    if (!window.matchMedia("(min-width: 1024px)").matches) return;
    const ric = (
      window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number }
    ).requestIdleCallback;
    if (ric) {
      const id = ric(() => setMountHeroVideo(true), { timeout: 2500 });
      return () => {
        const cancel = (window as unknown as { cancelIdleCallback?: (id: number) => void }).cancelIdleCallback;
        if (cancel) cancel(id);
      };
    }
    const t = window.setTimeout(() => setMountHeroVideo(true), 1500);
    return () => window.clearTimeout(t);
  }, [mountHeroVideo]);

  return (
    <div className="min-h-screen bg-background">
      {/* Branded top bar — logo links to main site (new tab), phone CTA right */}
      <header className="border-b border-border bg-secondary text-secondary-foreground">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3">
          <a
            href="https://coloradowebimpressions.com"
            target="_blank"
            rel="noopener"
            aria-label="Colorado Web Impressions — visit main site"
            className="flex items-center gap-3"
          >
            <img
              src={logo}
              alt="Colorado Web Impressions logo — Colorado Springs web design and SEO agency"
              className="h-10 w-auto sm:h-12"
              width={160}
              height={48}
              data-geo-lat="38.9359976"
              data-geo-lng="-104.7653118"
              data-geo-city="Colorado Springs"
            />

          </a>
          <a
            href="tel:+17194193935"
            className="inline-flex items-center gap-2 rounded-md bg-gradient-gold px-4 py-2 text-sm font-bold uppercase tracking-wider text-brand-black shadow-elegant transition hover:opacity-90"
           aria-label="Call (719) 419-3935">
            <Phone className="h-4 w-4" />
            <span className="hidden sm:inline">(719) 419-3935</span>
            <span className="sm:hidden">Call now</span>
          </a>
        </div>
      </header>

      {/* HERO with form above the fold + cinematic Colorado background */}
      <section className="relative isolate overflow-hidden bg-gradient-dark text-brand-cream">
        {/* LCP image — paints immediately on every device */}
        <img
          src={heroImg}
          alt=""
          aria-hidden="true"
          width={1920}
          height={1280}
          decoding="async"
          // @ts-expect-error fetchpriority not yet typed
          fetchpriority="high"
          className="absolute inset-0 h-full w-full object-cover opacity-70"
        />
        {/* Desktop-only background video, lazy-mounted after idle */}
        {mountHeroVideo ? (
          <video
            src={heroVideoUrl}
            poster={heroImg}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            aria-hidden="true"
            ref={(el) => {
              if (el) {
                const p = el.play();
                if (p && typeof p.catch === "function") p.catch(() => { /* ignore */ });
              }
            }}
            className="absolute inset-0 h-full w-full object-cover opacity-70 animate-fade-in"
          />
        ) : null}
        <div className="absolute inset-0 bg-[image:var(--gradient-hero-overlay)]" />
        <div className="absolute inset-0 bg-gradient-radial-gold opacity-50" />
        <div className="relative mx-auto grid max-w-6xl gap-10 px-4 py-12 lg:grid-cols-2 lg:gap-12 lg:py-20">
          <div>
            <p className="inline-block rounded-full border border-brand-gold/40 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold">
              {eyebrow}
            </p>
            <h1 className="mt-5 text-3xl font-extrabold leading-[1.1] text-white sm:text-4xl lg:text-5xl">
              {headline}
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-brand-cream/85">
              {subheadline}
            </p>

            <ul className="mt-7 space-y-3">
              {bullets.map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-brand-gold" />
                  <span className="text-base text-brand-cream/95">{b}</span>
                </li>
              ))}
            </ul>

            <div className="mt-7 flex flex-wrap items-center gap-4 border-t border-white/10 pt-5 text-sm text-brand-cream/80">
              <div className="flex items-center gap-1.5">
                <Star className="h-4 w-4 fill-brand-gold text-brand-gold" />
                <Star className="h-4 w-4 fill-brand-gold text-brand-gold" />
                <Star className="h-4 w-4 fill-brand-gold text-brand-gold" />
                <Star className="h-4 w-4 fill-brand-gold text-brand-gold" />
                <Star className="h-4 w-4 fill-brand-gold text-brand-gold" />
              </div>
              <span>{trustLine}</span>
            </div>

            <div className="mt-7 flex flex-col gap-2 lg:hidden">
              <a
                href="tel:+17194193935"
                className="inline-flex items-center justify-center gap-2 rounded-md bg-gradient-gold px-6 py-3 text-sm font-bold uppercase tracking-wider text-brand-black shadow-elegant transition hover:opacity-90"
               aria-label="Call (719) 419-3935">
                <Phone className="h-4 w-4" />
                Call (719) 419-3935
              </a>
              <a
                href="#lead-form"
                className="inline-flex items-center justify-center gap-2 rounded-md border border-brand-gold/60 bg-transparent px-6 py-3 text-sm font-bold uppercase tracking-wider text-brand-gold transition hover:bg-brand-gold/10"
              >
                {ctaLabel}
              </a>
            </div>
          </div>

          <div id="lead-form" className="relative scroll-mt-24 rounded-2xl border border-border bg-card/95 p-5 text-foreground shadow-elegant backdrop-blur sm:p-7">
            <span id="mockup" className="absolute -top-24" aria-hidden="true" />
            <h2 className="text-2xl font-extrabold">{formHeading}</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {formSubheading}
            </p>
            <div className="mt-5">
              <ContactForm />
            </div>
            <input type="hidden" data-lp-source={slug} />
            <p className="sr-only">{prefillMessage}</p>
          </div>
        </div>
      </section>

      {/* Optional welcome / intro video — lazy-mounted YouTube facade */}
      {videoYouTubeId ? (
        <section id="video" className="scroll-mt-24 bg-background py-10 lg:py-14">
          <div className="mx-auto max-w-3xl px-4">
            <h2 className="text-center text-2xl font-extrabold text-foreground sm:text-3xl">
              {videoHeading}
            </h2>
            <p className="mt-2 text-center text-sm text-muted-foreground">
              A quick hello and a look at how we work — no sales pitch.
            </p>
            <div className="mt-6 overflow-hidden rounded-2xl border border-border bg-card shadow-elegant">
              <div className="relative aspect-video w-full">
                {videoPlaying ? (
                  <iframe
                    src={`https://www.youtube-nocookie.com/embed/${videoYouTubeId}?rel=0&modestbranding=1&autoplay=1`}
                    title={videoHeading}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="absolute inset-0 h-full w-full"
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => setVideoPlaying(true)}
                    aria-label={`Play video: ${videoHeading}`}
                    className="group absolute inset-0 h-full w-full"
                  >
                    <img
                      src={`https://i.ytimg.com/vi/${videoYouTubeId}/maxresdefault.jpg`}
                      alt={videoHeading}
                      loading="lazy"
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                    <span className="absolute inset-0 bg-brand-black/30 transition group-hover:bg-brand-black/40" />
                    <span className="absolute inset-0 grid place-items-center">
                      <span className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-gold text-brand-black shadow-elegant transition group-hover:scale-110 sm:h-20 sm:w-20">
                        <Play className="h-7 w-7 fill-current sm:h-9 sm:w-9" />
                      </span>
                    </span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {/* Reviews trust strip */}
      <section className="border-y border-border bg-section-alt py-5">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-center gap-2 px-4 text-center sm:flex-row sm:gap-4">
          <div className="flex items-center gap-1">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star key={i} className="h-5 w-5 fill-brand-gold text-brand-gold" aria-hidden="true" />
            ))}
          </div>
          <div className="text-sm font-semibold text-foreground sm:text-base">
            4.9 / 5 · <span className="text-brand-gold">150+ verified 5-star reviews</span> across Google, Facebook &amp; BBB
          </div>
        </div>
      </section>

      {/* Per-LP testimonial — industry-matched social proof at the moment of decision */}
      {testimonial ? (
        <section className="bg-background py-10 lg:py-14">
          <div className="mx-auto max-w-3xl px-4">
            <figure className="rounded-2xl border border-border bg-card p-6 shadow-card sm:p-8">
              <Quote
                className="h-7 w-7 flex-shrink-0 text-brand-gold"
                aria-hidden="true"
              />
              <div className="mt-3 flex items-center gap-1">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    className="h-4 w-4 fill-brand-gold text-brand-gold"
                    aria-hidden="true"
                  />
                ))}
              </div>
              <blockquote className="mt-3 text-lg italic leading-relaxed text-foreground sm:text-xl">
                &ldquo;{testimonial.quote}&rdquo;
              </blockquote>
              <figcaption className="mt-4 text-sm text-muted-foreground">
                <span className="font-semibold text-foreground">
                  {testimonial.author}
                </span>
                {testimonial.location ? <span> · {testimonial.location}</span> : null}
                {testimonial.service ? (
                  <span className="block text-xs uppercase tracking-wider text-brand-gold/90">
                    {testimonial.service}
                  </span>
                ) : null}
              </figcaption>
            </figure>
          </div>
        </section>
      ) : null}

      {/* Deliverables */}
      <section className="bg-background py-14 lg:py-20">
        <div className="mx-auto max-w-6xl px-4">
          <h2 className="text-center text-3xl font-extrabold text-foreground sm:text-4xl">
            What you get
          </h2>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {deliverables.map((d) => (
              <div
                key={d.title}
                className="rounded-2xl border border-border bg-card p-6 shadow-card"
              >
                <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-lg bg-brand-gold/15 text-brand-gold">
                  <ShieldCheck className="h-5 w-5" />
                </div>
                <h3 className="text-lg font-bold text-foreground">{d.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{d.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div id="pricing" className="scroll-mt-24"><PricingTeaserStrip /></div>

      {/* Why us strip */}
      <section className="bg-secondary py-12 text-secondary-foreground">
        <div className="mx-auto grid max-w-6xl gap-6 px-4 sm:grid-cols-3">
          {[
            { icon: ShieldCheck, t: "Month-to-month", d: "No long contracts. Cancel anytime." },
            { icon: Clock, t: "Same-day reply", d: "Real human owner answers, not a bot." },
            { icon: Star, t: "Colorado-based", d: "Not outsourced. Built right here in Colorado Springs." },
          ].map((b) => (
            <div key={b.t} className="flex items-start gap-4">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-brand-gold/20 text-brand-gold">
                <b.icon className="h-5 w-5" />
              </div>
              <div>
                <div className="text-base font-bold text-brand-cream">{b.t}</div>
                <div className="mt-1 text-sm text-brand-cream/85">{b.d}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-background py-14 lg:py-20">
        <div className="mx-auto max-w-3xl px-4">
          <h2 className="text-center text-3xl font-extrabold text-foreground sm:text-4xl">
            Common questions
          </h2>
          <Accordion type="single" collapsible className="mt-8">
            {faqs.map((f, i) => (
              <AccordionItem key={f.q} value={`faq-${i}`}>
                <AccordionTrigger className="text-left text-base font-semibold">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA — anchored for sitelink #contact */}
      <section id="contact" className="scroll-mt-24 bg-gradient-dark py-14 text-brand-cream lg:py-20">
        <div className="mx-auto max-w-3xl px-4 text-center">
          <h2 className="text-3xl font-extrabold text-white sm:text-4xl">
            Claim your {offerNoun}
          </h2>
          <p className="mt-4 text-base text-brand-cream/85">
            No cost, no obligation, no high-pressure sales call. Call now or scroll up to send a quick message — Chris replies same business day.
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <a
              href="tel:+17194193935"
              className="inline-flex items-center justify-center gap-2 rounded-md bg-gradient-gold px-7 py-3.5 text-sm font-bold uppercase tracking-wider text-brand-black shadow-elegant transition hover:opacity-90"
             aria-label="Call (719) 419-3935">
              <Phone className="h-4 w-4" />
              Call (719) 419-3935
            </a>
            <a
              href="#lead-form"
              className="inline-flex items-center justify-center gap-2 rounded-md border border-brand-gold/60 bg-transparent px-7 py-3.5 text-sm font-bold uppercase tracking-wider text-brand-gold transition hover:bg-brand-gold/10"
            >
              {ctaLabel}
            </a>
          </div>
        </div>
      </section>

      {/* Minimal compliance footer — required by Google Ads policy */}
      <footer className="border-t border-border bg-secondary py-6 text-secondary-foreground">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 px-4 text-xs text-brand-cream/85 sm:flex-row">
          <div>
            © {new Date().getFullYear()} Colorado Web Impressions · Colorado Springs, CO
          </div>
          <div className="flex gap-4">
            <Link to="/privacy" className="hover:text-brand-gold">Privacy</Link>
            <Link to="/terms" className="hover:text-brand-gold">Terms</Link>
            <a href="tel:+17194193935" className="hover:text-brand-gold" aria-label="Call (719) 419-3935">
              (719) 419-3935
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

/**
 * Build the head() meta block for a PPC landing page. Always noindex/nofollow.
 * Each LP self-canonicals (no consolidation away — that hurts Quality Score).
 */
export function buildPpcHead({
  slug,
  title,
  description,
}: {
  slug: string;
  title: string;
  description: string;
}) {
  const url = `https://coloradowebimpressions.com/lp/${slug}`;
  return {
    meta: [
      { title },
      { name: "description", content: description },
      // Critical: keep PPC LPs out of organic index but let Google Ads bot crawl.
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:url", content: url },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
    ],
    links: [{ rel: "canonical", href: url }],
  };
}
